import {
  WorkInjuryAcceptedCoverageType,
  WorkInjuryEmployerLiabilityBundle
} from "@wingspanhq/benefits/dist/src/lib/interfaces/insuranceRequirement";
import { DefinitionDataSourceType } from "@wingspanhq/payments/dist/interfaces/requirement";
import type { insuranceMonitorSettingsApiSpec } from "@wingspanhq/wingspan-api-types";
import * as Yup from "yup";

import { selectorWorkersCompLimitMapper } from "../../../../selectors/selectorWorkersCompLimitMapper";
import { selectorWorkersCompLimitReverse } from "../../../../selectors/selectorWorkersCompLimitReverse";
import { RequirementDefinitionWorkInjuryFormValues } from "../../../../types";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import {
  FormPartialWorkInjury,
  WORKER_OCCUPATIONAL_ACCIDENT_AD_AND_D_MINIMUM_COMMON,
  WORKER_OCCUPATIONAL_ACCIDENT_MINIMUM_COMMON
} from "../FormPartialWorkInjury";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
 */

export const WORK_INJURY_FORM: RequirementDefinitionTemplateForm<
  insuranceMonitorSettingsApiSpec.InsuranceCoverageMonitorSettings,
  RequirementDefinitionWorkInjuryFormValues,
  insuranceMonitorSettingsApiSpec.CreateInsuranceCoverageMonitorSettingsRequest
> = {
  getDefaultSettingsValues: template => ({
    acceptWingspanMasterPolicy: true,
    coverageType: WorkInjuryAcceptedCoverageType.OccupationalAccident,
    name: template.name,
    occAccidentDeathMinimum: `${WORKER_OCCUPATIONAL_ACCIDENT_AD_AND_D_MINIMUM_COMMON}`,
    occAccidentMedicalMinimum: `${WORKER_OCCUPATIONAL_ACCIDENT_MINIMUM_COMMON}`,
    workersCompLimitMinimum:
      WorkInjuryEmployerLiabilityBundle.OneHundredFiveHundredOneHundred
  }),
  getEditSettingsValues(template, requirementDefinition, settings) {
    const wcCoverage = settings.coverages.find(
      c => c.policyType === "WorkersCompensation"
    );
    const oaCoverage = settings.coverages.find(
      c => c.policyType === "OccupationalAccident"
    );
    const hasWc = !!wcCoverage;
    const hasOa =
      !!oaCoverage &&
      !!oaCoverage.acceptedSourceTypes?.includes("InsuranceImport");
    const acceptWingspan =
      oaCoverage?.acceptedSourceTypes?.includes("PlanEnrollment") ?? false;

    const coverageType =
      hasWc && hasOa
        ? WorkInjuryAcceptedCoverageType.Either
        : hasWc
          ? WorkInjuryAcceptedCoverageType.WorkersCompensation
          : WorkInjuryAcceptedCoverageType.OccupationalAccident;

    const medicalLimit = oaCoverage?.limits?.find(
      l => l.limitType === "MedicalExpensePerPerson"
    );
    const deathLimit = oaCoverage?.limits?.find(
      l => l.limitType === "PerAccident"
    );

    return {
      acceptWingspanMasterPolicy: acceptWingspan,
      coverageType,
      occAccidentDeathMinimum: deathLimit
        ? String(deathLimit.amount)
        : undefined,
      occAccidentMedicalMinimum: medicalLimit
        ? String(medicalLimit.amount)
        : undefined,
      workersCompLimitMinimum: wcCoverage
        ? selectorWorkersCompLimitReverse(wcCoverage.limits ?? [])
        : undefined
    };
  },
  mapDataSourceResponseToRequirementCreateRequest(response) {
    return {
      dataSourceId: response.insuranceCoverageMonitorSettingsId,
      dataSourceType: DefinitionDataSourceType.InsuranceCoverageMonitorSettings
    };
  },
  mapFormDataToDataSourceCreateRequest(values) {
    const coverages: insuranceMonitorSettingsApiSpec.AcceptedInsuranceCoverage[] =
      [];

    const {
      acceptWingspanMasterPolicy,
      coverageType,
      workersCompLimitMinimum,
      occAccidentMedicalMinimum,
      occAccidentDeathMinimum,
      ...other
    } = values;

    if (
      coverageType === WorkInjuryAcceptedCoverageType.WorkersCompensation ||
      coverageType === WorkInjuryAcceptedCoverageType.Either
    ) {
      coverages.push({
        acceptedSourceTypes: ["InsuranceImport"],
        appliedProvisions: [],
        limits: selectorWorkersCompLimitMapper(workersCompLimitMinimum),
        policyType: "WorkersCompensation"
      });
    }

    if (
      coverageType === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
      coverageType === WorkInjuryAcceptedCoverageType.Either
    ) {
      coverages.push({
        acceptedSourceTypes: ["InsuranceImport"],
        appliedProvisions: [],
        limits: [
          {
            amount: Number(occAccidentMedicalMinimum),
            currency: "USD",
            limitType: "MedicalExpensePerPerson"
          },
          {
            amount: Number(occAccidentDeathMinimum),
            currency: "USD",
            limitType: "PerAccident"
          }
        ],
        policyType: "OccupationalAccident"
      });
    }

    if (acceptWingspanMasterPolicy) {
      // only occupational accident master policy is supported by wingspan for now
      const occupationalAccident = coverages.find(
        c => c.policyType === "OccupationalAccident"
      );
      if (occupationalAccident) {
        // add plan enrollment to the already existing occupational accident coverage
        occupationalAccident.acceptedSourceTypes?.push("PlanEnrollment");
      } else {
        // add a coverage which accepts only plan enrollment for occupational accident
        coverages.push({
          acceptedSourceTypes: ["PlanEnrollment"],
          appliedProvisions: [],
          limits: [],
          policyType: "OccupationalAccident"
        });
      }
    }

    return {
      ...other,
      coverages
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's OAI Master Policy" />
      <FormPartialWorkInjury />
    </>
  ),
  successCreationMessage: "Insurance coverage requirement created successfully",
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    coverageType: Yup.string()
      .oneOf(Object.values(WorkInjuryAcceptedCoverageType))
      .required("Required") as Yup.AnySchema,
    occAccidentDeathMinimum: Yup.string().when("coverageType", {
      is: (val: WorkInjuryAcceptedCoverageType) =>
        val === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
        val === WorkInjuryAcceptedCoverageType.Either,
      otherwise: Yup.string(),
      then: Yup.string().required("Required")
    }) as Yup.AnySchema,
    occAccidentMedicalMinimum: Yup.string().when("coverageType", {
      is: (val: string) =>
        val === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
        val === WorkInjuryAcceptedCoverageType.Either,
      otherwise: Yup.string(),
      then: Yup.string().required("Required")
    }),
    workersCompLimitMinimum: Yup.string().when("coverageType", {
      is: (val: WorkInjuryAcceptedCoverageType) =>
        val === WorkInjuryAcceptedCoverageType.WorkersCompensation ||
        val === WorkInjuryAcceptedCoverageType.Either,
      otherwise: Yup.string(),
      then: Yup.string()
        .oneOf(Object.values(WorkInjuryEmployerLiabilityBundle))
        .required("Required")
    }) as Yup.AnySchema
  })
};
